import React, { FormEvent, useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { Rifm } from 'rifm';

import { RootState } from '../../../init-store';
import Button from '../../shared/components/button';
import StartScreenPopup from '../../shared/components/start-screen-popup';
import TextInput from '../../shared/components/text-input';
import { i18n } from '../../shared/helpers/i18n';
import { countMetrika } from '../../shared/helpers/metrika';
import { getUrl, SCREENS } from '../../shared/helpers/navigation';
import style from './connect-to-meeting-by-id.css';

const LINK_REGEXP = /https?:\/\/.*\/j\/(\d*)/;
const NUMBER_REGEXP = /^\d*$/;
const STRING_GROUP_REGEXP = /.{1,4}/g;

const mapState = ({ sessionId }: RootState) => ({ sessionId });

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

const normalizeMeetingId = (formattedMeetingId: string) => formattedMeetingId.replace(/\s/g, '');

const navigateToMeeting = (id: string) => {
    window.location.href = getUrl(SCREENS.CALL, { id, searchString: window.location.search });
};

const ConnectToMeetingById: React.FC<Props> = ({ sessionId }) => {
    const [meetingId, setMeetingId] = useState('');
    const [hasError, setHasError] = useState(false);

    const formatId = useCallback(
        (inputValue: string): string => {
            const value = normalizeMeetingId(inputValue);
            const linkMatch = value.match(LINK_REGEXP);
            const id = linkMatch?.[1] || value;
            return NUMBER_REGEXP.test(id) ? id.match(STRING_GROUP_REGEXP)?.join(' ') || '' : meetingId;
        },
        [meetingId]
    );

    const onSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();

            countMetrika('main_screen', 'connect_by_id_screen', 'connect', sessionId);

            if (meetingId) {
                navigateToMeeting(normalizeMeetingId(meetingId));
            } else {
                countMetrika('main_screen', 'connect_by_id_screen', 'error', sessionId);
                setHasError(false);
                setTimeout(() => {
                    // we need to remove class, rerender and then add class again for correct animation
                    setHasError(true);
                });
            }
        },
        [meetingId, sessionId]
    );

    useEffect(() => {
        countMetrika('main_screen', 'connect_by_id_screen', 'show', sessionId);
    }, [sessionId]);

    return (
        <StartScreenPopup className={style.startScreenPopup} blur>
            <div className={style.root}>
                <form onSubmit={onSubmit} className={style.content}>
                    <div className={style.title}>{i18n('create-call', 'insert-link-or-meeting-number')}</div>
                    <Rifm format={formatId} value={meetingId} onChange={setMeetingId}>
                        {({ value, onChange }) => (
                            <TextInput
                                size="lg"
                                className={cn(style.meetingIdInputWrapper, {
                                    [style.hasError]: hasError,
                                })}
                                onChange={onChange}
                                value={value}
                                autoFocus
                            />
                        )}
                    </Rifm>
                    <Button
                        type="submit"
                        view="brand"
                        size="lg"
                        className={cn(style.connectButton, {
                            [style.emptyInput]: !meetingId,
                        })}
                    >
                        {i18n('create-call', 'connect')}
                    </Button>
                </form>
            </div>
        </StartScreenPopup>
    );
};

export const ConnectToMeetingByIdPage = connector(ConnectToMeetingById);
